import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  FiAlertTriangle,
  FiCheck,
  FiCopy,
  FiHeart,
  FiPhone,
  FiPlay,
  FiTarget,
  FiRefreshCw,
  FiTrash2,
  FiX,
  FiZap,
} from "react-icons/fi";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import Avatar from "./Avatar";
import "./SeguimientoPanel.css";

/*
 * Lo que la plataforma dice a los clientes en nombre del entrenador.
 *
 * Esta pantalla existe por una razón: si el sistema escribe a tus clientes,
 * tienes que poder ver exactamente qué les ha dicho, quién ha contestado y
 * poder apagarlo. Lo primero que se enseña es la cola —lo que saldría ahora—
 * para que nada salga a ciegas.
 */

const auth = async () => ({ Authorization: `Bearer ${(await getSession()).token}` });

const fecha = (d) =>
  d
    ? new Date(d).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

const tardanza = (min) => {
  if (min == null) return null;
  if (min < 60) return `${min} min`;
  if (min < 60 * 24) return `${Math.round(min / 60)} h`;
  return `${Math.round(min / 1440)} días`;
};

function SeguimientoPanel() {
  const [datos, setDatos] = useState(null);
  const [cola, setCola] = useState(null);
  const [whatsapp, setWhatsapp] = useState(null);
  const [plantillas, setPlantillas] = useState(null);
  const [atencion, setAtencion] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [retos, setRetos] = useState([]);
  const [reto, setReto] = useState(null);
  const [vista, setVista] = useState("cola");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [copiada, setCopiada] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const headers = await auth();
      const [h, c, w, p, a, av, rt] = await Promise.all([
        axios.get(`${API_URL}/seguimiento`, { headers }),
        axios.get(`${API_URL}/seguimiento/cola`, { headers }),
        axios.get(`${API_URL}/whatsapp/estado`, { headers }),
        axios.get(`${API_URL}/whatsapp/plantillas`, { headers }),
        axios.get(`${API_URL}/seguimiento/atencion`, { headers }),
        axios.get(`${API_URL}/habitos/avisos`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/comunidad/retos`, { headers }).catch(() => ({ data: [] })),
      ]);
      setDatos(h.data);
      setCola(c.data);
      setWhatsapp(w.data);
      setPlantillas(p.data);
      setAtencion(a.data);
      setAvisos(av.data);
      setRetos(rt.data);
      setReto((r) => r || rt.data[0] || null);
      setError("");
    } catch {
      setError("No se pudo cargar el seguimiento.");
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const accion = async (fn) => {
    setOcupado(true);
    setError("");
    try {
      await fn(await auth());
      await cargar();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo completar.");
    }
    setOcupado(false);
  };

  const lanzar = () =>
    accion((headers) => axios.post(`${API_URL}/seguimiento/ejecutar`, {}, { headers }));

  const conmutar = () =>
    accion((headers) =>
      axios.patch(`${API_URL}/seguimiento/activo`, { activo: !datos.activo }, { headers })
    );

  const retirar = (id) =>
    accion((headers) => axios.delete(`${API_URL}/seguimiento/${id}`, { headers }));

  const atendido = (id) =>
    accion((headers) => axios.patch(`${API_URL}/habitos/avisos/${id}/visto`, {}, { headers }));

  const guardarReto = (campos) =>
    accion((headers) => axios.put(`${API_URL}/comunidad/reto`, campos, { headers }));

  const copiar = async (texto, clave) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiada(clave);
      setTimeout(() => setCopiada(null), 1800);
    } catch {
      /* sin portapapeles: el texto está a la vista para copiarlo a mano */
    }
  };

  if (!datos) {
    return <div className="app-estado">{error || "Cargando seguimiento…"}</div>;
  }

  const r = datos.resumen;

  return (
    <div className="sp">
      {/* Interruptor y números */}
      <header className="sp-top">
        <div className="sp-interruptor">
          <button
            type="button"
            className={datos.activo ? "sp-switch sp-switch-on" : "sp-switch"}
            onClick={conmutar}
            disabled={ocupado}
            aria-pressed={datos.activo}
          >
            <span className="sp-switch-bola" />
          </button>
          <div>
            <strong>{datos.activo ? "Seguimiento activo" : "Seguimiento apagado"}</strong>
            <span>
              {datos.activo
                ? `Se repasa cada hora, de 9:00 a 21:59. Como mucho un mensaje cada ${datos.ajustes.cadenciaDias} días por cliente.`
                : "No se enviará nada automáticamente hasta que lo vuelvas a encender."}
            </span>
          </div>
        </div>

        <div className="sp-acciones">
          <button type="button" className="sp-btn-plano" onClick={cargar} disabled={ocupado}>
            <FiRefreshCw /> Actualizar
          </button>
          <button type="button" className="sp-btn" onClick={lanzar} disabled={ocupado || !cola?.enviados.length}>
            <FiPlay /> Enviar ahora ({cola?.enviados.length || 0})
          </button>
        </div>
      </header>

      <div className="sp-numeros">
        {[
          ["Mensajes enviados", r.total],
          ["Han contestado", `${r.contestados} (${r.tasaRespuesta}%)`],
          ["Tardan de media", tardanza(r.tardanzaMediaMin) || "—"],
          ["Con WhatsApp", whatsapp ? `${whatsapp.conPermiso} de ${whatsapp.total}` : "—"],
        ].map(([t, v]) => (
          <div className="sp-numero" key={t}>
            <span>{t}</span>
            <strong>{v}</strong>
          </div>
        ))}
      </div>

      {error && <p className="sp-error">{error}</p>}

      {/*
        Lo primero de la pantalla, por delante de todo lo demás: si alguien ha
        escrito algo que preocupa, eso importa más que cualquier métrica.
      */}
      {avisos.filter((a) => !a.visto).length > 0 && (
        <section className="sp-bienestar">
          <h3>
            <FiHeart aria-hidden="true" /> Alguien no está bien
          </h3>
          <p>
            Ha salido en su check-in diario. Esto no lo arregla un mensaje
            automático: llama tú.
          </p>
          <ul>
            {avisos
              .filter((a) => !a.visto)
              .map((a) => (
                <li key={a._id} className={`sp-aviso-${a.nivel}`}>
                  <div className="sp-bienestar-top">
                    <strong>{a.cliente.nombre}</strong>
                    <span className="sp-tipo">
                      {a.nivel === "grave" ? "Urgente" : a.nivel === "comida" ? "Comida" : "Ánimo bajo"}
                    </span>
                    <span className="sp-cuando">{a.dia}</span>
                  </div>
                  <p className="sp-bienestar-aviso">{a.aviso}</p>
                  {a.nota && <blockquote>“{a.nota}”</blockquote>}
                  <div className="sp-bienestar-acciones">
                    {a.cliente.telefono && (
                      <a className="sp-tel" href={`tel:${a.cliente.telefono}`}>
                        <FiPhone aria-hidden="true" /> {a.cliente.telefono}
                      </a>
                    )}
                    <button type="button" onClick={() => atendido(a._id)} disabled={ocupado}>
                      Ya he hablado con él
                    </button>
                  </div>
                </li>
              ))}
          </ul>
        </section>
      )}

      {/* A quién hay que llamar: lo que el automático no arregla */}
      {atencion.length > 0 && (
        <section className="sp-llamar">
          <h3>
            <FiPhone aria-hidden="true" /> Estos necesitan una llamada, no otro mensaje
          </h3>
          <p>Llevan dos o más mensajes sin contestar. A partir de aquí, el automático ya no sirve.</p>
          <ul>
            {atencion.map((a) => (
              <li key={a._id}>
                <Avatar
                  userId={a._id}
                  nombre={a.cliente.firstName}
                  apellido={a.cliente.lastName}
                  tamano={36}
                />
                <div>
                  <strong>
                    {[a.cliente.firstName, a.cliente.lastName].filter(Boolean).join(" ") || a.cliente.email}
                  </strong>
                  <span>
                    {a.veces} mensajes sin respuesta · último {fecha(a.ultimo)}
                  </span>
                </div>
                {a.cliente.phone && (
                  <a className="sp-tel" href={`tel:${a.cliente.phone}`}>
                    {a.cliente.phone}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Pestañas */}
      <nav className="sp-pestanas">
        {[
          ["cola", `Lo que saldría ahora (${cola?.enviados.length || 0})`],
          ["historial", `Enviados (${datos.registros.length})`],
          ["rendimiento", "Qué mensaje funciona"],
          ["whatsapp", "WhatsApp"],
          ["comunidad", "Reto del mes"],
        ].map(([k, t]) => (
          <button
            key={k}
            type="button"
            className={vista === k ? "sp-pestana sp-pestana-on" : "sp-pestana"}
            onClick={() => setVista(k)}
          >
            {t}
          </button>
        ))}
      </nav>

      {/* ── La cola ── */}
      {vista === "cola" && (
        <section className="sp-lista">
          {!cola?.enviados.length ? (
            <p className="sp-vacio">
              Ahora mismo no hay nada que enviar. O todos están al día, o se les escribió
              hace menos de {datos.ajustes.cadenciaDias} días.
            </p>
          ) : (
            cola.enviados.map((e) => (
              <article className="sp-msg" key={e.userId + e.tipo}>
                <header>
                  <strong>{e.nombre || "Cliente"}</strong>
                  <span className="sp-tipo">{e.tipo}</span>
                </header>
                <p>{e.texto}</p>
              </article>
            ))
          )}

          {/* Por qué a los demás no les llega nada: es la mitad del valor de esta pantalla */}
          {cola?.saltados?.length > 0 && (
            <details className="sp-saltados">
              <summary>Por qué a los otros {cola.saltados.length} no les sale nada</summary>
              <ul>
                {cola.saltados.map((s) => (
                  <li key={s.userId}>
                    <b>{s.nombre || "Cliente"}</b>
                    <span>{s.motivo}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      {/* ── Historial ── */}
      {vista === "historial" && (
        <section className="sp-lista">
          {!datos.registros.length ? (
            <p className="sp-vacio">Todavía no se ha enviado ningún mensaje automático.</p>
          ) : (
            datos.registros.map((reg) => (
              <article className={reg.respondido ? "sp-msg sp-msg-ok" : "sp-msg"} key={reg._id}>
                <header>
                  <strong>{reg.cliente?.nombre || "Cliente"}</strong>
                  <span className="sp-tipo">{reg.etiqueta}</span>
                  <span className="sp-cuando">{fecha(reg.enviadoEn)}</span>
                  {reg.mensajeId && (
                    <button
                      type="button"
                      className="sp-retirar"
                      onClick={() => retirar(reg._id)}
                      disabled={ocupado}
                      title="Borrar este mensaje del chat del cliente"
                      aria-label="Retirar mensaje"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </header>
                <p>{reg.texto}</p>
                <footer>
                  {reg.respondido ? (
                    <span className="sp-ok">
                      <FiCheck aria-hidden="true" /> Contestó
                      {reg.tardanzaMin != null && ` en ${tardanza(reg.tardanzaMin)}`}
                    </span>
                  ) : (
                    <span className="sp-pendiente">Sin respuesta</span>
                  )}
                  {reg.whatsapp && (
                    <span className={reg.whatsapp === "enviado" ? "sp-wa sp-wa-ok" : "sp-wa"}>
                      <FiZap aria-hidden="true" /> WhatsApp: {reg.whatsapp}
                    </span>
                  )}
                </footer>
              </article>
            ))
          )}
        </section>
      )}

      {/* ── Rendimiento ── */}
      {vista === "rendimiento" && (
        <section className="sp-rendimiento">
          <p className="sp-nota">
            Qué proporción de cada tipo de mensaje se contesta. El que no se
            contesta no sirve: cambia el texto o quítalo.
          </p>
          {!r.porTipo.length ? (
            <p className="sp-vacio">Aún no hay datos suficientes.</p>
          ) : (
            <ul className="sp-barras">
              {r.porTipo.map((t) => {
                const pct = t.enviados ? Math.round((t.contestados / t.enviados) * 100) : 0;
                return (
                  <li key={t.tipo}>
                    <div className="sp-barra-top">
                      <b>{t.etiqueta}</b>
                      <span>
                        {t.contestados} de {t.enviados} · {pct}%
                      </span>
                    </div>
                    <div className="sp-barra">
                      <div style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ── Reto de la comunidad ── */}
      {vista === "comunidad" && (
        <section className="sp-reto-editor">
          <p className="sp-nota">
            Una meta común para todos, no una clasificación. Con pocos clientes,
            un ranking motiva a los dos de arriba y hunde al resto; una suma
            entre todos hace que el entreno de cada uno cuente para los demás.
          </p>

          <div className="sp-reto-campos">
            <label>
              <span>Mes</span>
              <input
                type="month"
                value={reto?.mes || new Date().toISOString().slice(0, 7)}
                onChange={(e) => setReto({ ...reto, mes: e.target.value })}
              />
            </label>
            <label>
              <span>Título</span>
              <input
                type="text"
                maxLength={120}
                placeholder="100 entrenos entre todos"
                value={reto?.titulo || ""}
                onChange={(e) => setReto({ ...reto, titulo: e.target.value })}
              />
            </label>
            <label>
              <span>Qué se suma</span>
              <select
                value={reto?.metrica || "entrenos"}
                onChange={(e) => setReto({ ...reto, metrica: e.target.value })}
              >
                <option value="entrenos">Entrenos</option>
                <option value="minutos">Minutos</option>
                <option value="volumen">Kilos movidos</option>
              </select>
            </label>
            <label>
              <span>Objetivo</span>
              <input
                type="number"
                min="1"
                value={reto?.objetivo || 100}
                onChange={(e) => setReto({ ...reto, objetivo: Number(e.target.value) })}
              />
            </label>
            <label className="sp-ancho">
              <span>Descripción</span>
              <input
                type="text"
                maxLength={600}
                placeholder="Si llegamos, lo celebramos"
                value={reto?.descripcion || ""}
                onChange={(e) => setReto({ ...reto, descripcion: e.target.value })}
              />
            </label>
            <label className="sp-ancho">
              <span>Qué pasa si llegáis</span>
              <input
                type="text"
                maxLength={300}
                placeholder="Desayuno en la playa invita la casa"
                value={reto?.recompensa || ""}
                onChange={(e) => setReto({ ...reto, recompensa: e.target.value })}
              />
            </label>
          </div>

          <button
            type="button"
            className="sp-btn"
            disabled={ocupado || !reto?.titulo}
            onClick={() => guardarReto(reto)}
          >
            <FiTarget /> Guardar reto
          </button>

          {retos.length > 0 && (
            <>
              <h3>Retos anteriores</h3>
              <ul className="sp-retos">
                {retos.map((r) => (
                  <li key={r._id}>
                    <button type="button" onClick={() => setReto(r)}>
                      <b>{r.mes}</b>
                      <span>{r.titulo}</span>
                      {r.progreso && (
                        <em className={r.progreso.conseguido ? "sp-ok" : ""}>
                          {r.progreso.suma} / {r.objetivo} · {r.progreso.porcentaje}%
                        </em>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {/* ── WhatsApp ── */}
      {vista === "whatsapp" && whatsapp && (
        <section className="sp-wa-panel">
          {whatsapp.configurado ? (
            <div className="sp-aviso sp-aviso-ok">
              <FiCheck aria-hidden="true" />
              <div>
                <strong>WhatsApp conectado</strong>
                <p>
                  {whatsapp.conPermiso} de {whatsapp.total} clientes han dado permiso. Al
                  resto sólo les llega el mensaje dentro de la app.
                </p>
              </div>
            </div>
          ) : (
            <div className="sp-aviso">
              <FiAlertTriangle aria-hidden="true" />
              <div>
                <strong>WhatsApp todavía no está conectado</strong>
                <p>
                  Falta por configurar:{" "}
                  {whatsapp.falta.map((f, i) => (
                    <span key={f}>
                      {i > 0 && ", "}
                      <code>{f}</code>
                    </span>
                  ))}
                  . Mientras, el seguimiento funciona igual por el chat de la app.
                </p>
              </div>
            </div>
          )}

          {whatsapp.errores?.length > 0 && (
            <div className="sp-aviso sp-aviso-mal">
              <FiX aria-hidden="true" />
              <div>
                <strong>Errores de Meta</strong>
                <ul>
                  {whatsapp.errores.map((e) => (
                    <li key={e.error}>
                      {e.error} <b>×{e.veces}</b>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {whatsapp.urlWebhook && (
            <div className="sp-webhook">
              <span>URL del webhook para pegar en Meta</span>
              <div>
                <code>{whatsapp.urlWebhook}</code>
                <button type="button" onClick={() => copiar(whatsapp.urlWebhook, "webhook")}>
                  {copiada === "webhook" ? <FiCheck /> : <FiCopy />}
                  {copiada === "webhook" ? "Copiado" : "Copiar"}
                </button>
              </div>
              <small>
                Es lo que recibe las respuestas y las bajas. Sin esto, quien
                responda BAJA seguirá recibiendo mensajes.
              </small>
            </div>
          )}

          <h3>Plantillas que hay que dar de alta en Meta</h3>
          <p className="sp-nota">
            Fuera de la ventana de 24 horas, WhatsApp sólo deja enviar plantillas
            aprobadas de antemano. Copia cada texto tal cual en el gestor de
            plantillas de Meta, con ese mismo nombre, idioma español y categoría.
            Si el texto no coincide, Meta rechaza el envío.
          </p>
          <ul className="sp-plantillas">
            {plantillas?.map((p) => (
              <li key={p.nombre}>
                <div className="sp-plantilla-top">
                  <code>{p.nombre}</code>
                  <span className="sp-tipo">{p.categoria}</span>
                  <button type="button" onClick={() => copiar(p.texto, p.nombre)}>
                    {copiada === p.nombre ? <FiCheck /> : <FiCopy />}
                    {copiada === p.nombre ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <p>{p.texto}</p>
                <small>
                  Variables: {p.variables.join(", ")}
                  {p.usadaPara.length > 0 && ` · se usa para: ${p.usadaPara.join(", ")}`}
                </small>
              </li>
            ))}
          </ul>

          {whatsapp.envios?.length > 0 && (
            <>
              <h3>Últimos envíos</h3>
              <ul className="sp-envios">
                {whatsapp.envios.map((e) => (
                  <li key={e._id}>
                    <span className={`sp-estado sp-estado-${e.estado}`}>{e.estado}</span>
                    <b>{e.cliente}</b>
                    <code>{e.plantilla}</code>
                    <span className="sp-cuando">{fecha(e.createdAt)}</span>
                    {e.error && <em>{e.error}</em>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  );
}

export default SeguimientoPanel;
